import { formatMoney, type TimelineResponse } from "@vouch/shared";
import { CheckCircle2, Clock3, CircleDashed, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Card } from "./ui";

const CONFIRMED = new Set(["confirmed", "accepted", "active", "repaid", "paid"]);

export function FinancialTimeline({ data }: { data: TimelineResponse }): JSX.Element {
  const [confirmedOnly, setConfirmedOnly] = useState(true);
  const items = useMemo(
    () => (confirmedOnly ? data.items.filter((item) => CONFIRMED.has(item.status)) : data.items),
    [confirmedOnly, data.items],
  );

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-slate-800">Updates</h2>
          <p className="text-sm text-slate-500">A chronological record of confirmed and awaiting activity.</p>
        </div>
        <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={confirmedOnly}
            onChange={(event) => setConfirmedOnly(event.target.checked)}
            className="h-4 w-4 accent-blue-600"
          />
          Confirmed only
        </label>
      </div>

      {items.length === 0 ? (
        <Card><p className="text-sm text-slate-500">No {confirmedOnly ? "confirmed " : ""}updates yet.</p></Card>
      ) : (
        <div className="relative ml-3 border-l border-slate-200 pl-6">
          {items.map((item) => (
            <article key={item.id} className="relative pb-5 last:pb-0">
              <span className="absolute -left-[2.15rem] top-4 flex h-5 w-5 items-center justify-center rounded-full bg-white text-slate-400 ring-4 ring-slate-50">
                <StatusIcon status={item.status} />
              </span>
              <Card className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800">{item.title}</p>
                    {item.subtitle && <p className="mt-0.5 text-sm text-slate-500">{item.subtitle}</p>}
                    <p className="mt-1 text-xs text-slate-400">
                      {new Date(item.occurredAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="text-right">
                    {item.amountMinor != null && item.currency && (
                      <p className="font-semibold text-slate-800">{formatMoney(item.amountMinor, item.currency)}</p>
                    )}
                    <StatusChip status={item.status} />
                  </div>
                </div>
              </Card>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusIcon({ status }: { status: string }): JSX.Element {
  if (CONFIRMED.has(status)) return <CheckCircle2 size={18} className="text-emerald-600" />;
  if (status === "rejected" || status === "cancelled") return <XCircle size={18} className="text-rose-500" />;
  if (status.includes("pending")) return <Clock3 size={18} className="text-amber-500" />;
  return <CircleDashed size={18} />;
}

function StatusChip({ status }: { status: string }): JSX.Element {
  const style = CONFIRMED.has(status)
    ? "bg-emerald-50 text-emerald-700"
    : status === "rejected" || status === "cancelled"
      ? "bg-rose-50 text-rose-700"
      : "bg-amber-50 text-amber-700";
  return <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${style}`}>{status.replaceAll("_", " ")}</span>;
}
